import { Button } from '@form-factory/chatbot-ui';
import { useQuery } from '@tanstack/react-query';
import { Undo2 } from 'lucide-react';
import { useRef } from 'react';

import { useChatConfig } from '@/contexts/ChatConfigProvider';
import { useFAQ } from '@/hooks/useFAQ';
import type { UseChatReturn } from '@/routes/chat/chatTypes';
import { aiCoreApi } from '@/services/aiCoreClient';
import { faqKeys } from '@/services/queryClient';

import { FAQItem } from './FAQItem';

export interface FaqsProps {
  insert: UseChatReturn['insert'];
}

export function FAQs({ insert }: FaqsProps) {
  const ref = useRef<HTMLDivElement>(null);
  const { chatId } = useChatConfig();
  const { currentFAQId, canGoBack, navigateBack } = useFAQ();

  const { data } = useQuery({
    queryKey: [...faqKeys.chatLists(chatId), currentFAQId],
    queryFn: async () =>
      aiCoreApi.faqs.findAll({
        chatId,
        parentId: currentFAQId || null,
      }),
    enabled: !!chatId,
  });

  const handleGoBack = async () => {
    await navigateBack();
  };

  return (
    <div
      className='flex flex-auto flex-row flex-wrap justify-between gap-2 pb-2 pt-6'
      ref={ref}
    >
      <div className='flex flex-auto flex-col flex-wrap justify-end gap-2'>
        {data?.map(faq => (
          <div className='flex flex-row justify-end' key={faq.id}>
            <FAQItem
              title={faq.title}
              insert={insert}
              id={faq.id}
              question={faq.question}
              childrenCount={faq.childrenCount}
              answer={faq.answer}
            />
          </div>
        ))}
        {canGoBack && (
          <div className='flex flex-row justify-end'>
            <Button onClick={handleGoBack} variant='outline' circle>
              <Undo2 />
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
